import { PlayCircle, Clock } from "lucide-react";

/**
 * TutorialCard — a video tile inside Business Academy. Handles three states:
 *   1. No video_url yet → "Tutorial Coming Soon" placeholder
 *   2. YouTube URL   → embedded iframe
 *   3. Vimeo URL     → embedded iframe
 * Renders the same card shape in both empty and populated states so the
 * layout doesn't shift when admins add videos.
 */
function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
  );
  return m ? m[1] : null;
}

function extractVimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

export function TutorialEmbed({ videoUrl, thumbnailUrl, title }) {
  const yt = extractYouTubeId(videoUrl);
  const vm = !yt && extractVimeoId(videoUrl);
  if (yt) {
    return (
      <div className="aspect-video rounded-xl overflow-hidden bg-tbc-navy">
        <iframe
          src={`https://www.youtube.com/embed/${yt}?rel=0`}
          title={title || "Tutorial"}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    );
  }
  if (vm) {
    return (
      <div className="aspect-video rounded-xl overflow-hidden bg-tbc-navy">
        <iframe
          src={`https://player.vimeo.com/video/${vm}`}
          title={title || "Tutorial"}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    );
  }
  return (
    <div className="aspect-video rounded-xl bg-gradient-to-br from-tbc-navy to-tbc-navy/80 flex flex-col items-center justify-center border-2 border-dashed border-tbc-gold/25 text-center px-6">
      <div className="w-14 h-14 rounded-full bg-tbc-gold/10 text-tbc-gold flex items-center justify-center mb-3">
        <PlayCircle className="w-7 h-7" />
      </div>
      <p className="text-white font-heading text-base sm:text-lg">
        Tutorial Coming Soon
      </p>
      <p className="text-white/50 text-xs mt-1">
        We&apos;re recording this walkthrough — check back shortly.
      </p>
    </div>
  );
}

export default function TutorialCard({ tutorial, onOpen, className = "" }) {
  if (!tutorial) return null;
  const hasVideo = Boolean(tutorial.video_url);
  const minutes = tutorial.duration_seconds
    ? Math.max(1, Math.round(tutorial.duration_seconds / 60))
    : null;
  return (
    <button
      type="button"
      onClick={() => onOpen?.(tutorial)}
      className={`group text-left rounded-2xl overflow-hidden bg-white border border-gray-200 hover:border-tbc-gold/60 hover:shadow-md transition-all ${className}`}
      data-testid={`tutorial-card-${tutorial.slug}`}
    >
      <div className="relative aspect-video bg-gradient-to-br from-tbc-navy to-tbc-navy/80">
        {tutorial.thumbnail_url ? (
          <img
            src={tutorial.thumbnail_url}
            alt={tutorial.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <PlayCircle
              className={`w-12 h-12 ${
                hasVideo ? "text-tbc-gold" : "text-white/25"
              }`}
            />
          </div>
        )}
        {!hasVideo && (
          <span className="absolute top-2 left-2 bg-tbc-navy/80 text-white/70 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full">
            Coming soon
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="font-heading text-tbc-navy text-sm line-clamp-1">
          {tutorial.title}
        </p>
        {tutorial.description && (
          <p className="text-gray-600 text-xs mt-1 line-clamp-2">
            {tutorial.description}
          </p>
        )}
        {minutes && (
          <p className="text-gray-400 text-xs mt-2 inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {minutes} min
          </p>
        )}
      </div>
    </button>
  );
}
